import { Box } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- billing table uses bare DataGridPro for client-side totals + pinned-row footer; toolbar uses shared DataGridToolbar
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { addMonths, endOfDay, format, isValid, startOfDay } from 'date-fns';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import { fetchRawTotalsByDateAndSiteGroup } from '@sophocles/entities/ServicePerformed';
import { getTotalReducer } from '@sophocles/features/projectServices/utils';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import { formatNumberWithCommas } from '@sophocles/utils';

import InstallServicesBillingExcelButton from './InstallServicesBillingExcelButton';

function DateBillingTable({
  customerId,
  projectId,
  onChangeDateRange,
  startDate: externalStart,
  endDate: externalEnd,
  extraParams,
  extraToolbarContent,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  // Tenant-level term: the billing summary spans customers, so no single
  // CustomerType override applies — the tenant vocabulary does.
  const siteGroupTerm = useSiteGroupTerm();

  const [internalStart, setInternalStart] = useState(() => startOfDay(addMonths(new Date(), -1)));
  const [internalEnd, setInternalEnd] = useState(() => endOfDay(new Date()));

  const startDate = externalStart || internalStart;
  const endDate = externalEnd || internalEnd;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(
      fetchRawTotalsByDateAndSiteGroup({
        service_date__gte: format(startDate, 'yyyy-MM-dd'),
        service_date__lte: format(endDate, 'yyyy-MM-dd'),
        ...extraParams,
      }),
    )
      .then(res => {
        const data = res?.results || res?.payload?.results || (Array.isArray(res) ? res : []);
        setRows(data);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dispatch, startDate, endDate, extraParams]);

  const handleDateRangeChange = useCallback(
    (start, end) => {
      setInternalStart(start);
      setInternalEnd(end);
      if (onChangeDateRange) onChangeDateRange(start, end);
    },
    [onChangeDateRange],
  );

  useEffect(() => {
    if (onChangeDateRange) onChangeDateRange(startDate, endDate);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'service_date',
        headerName: t('projectServices.billing.columns.installDate'),
        width: 130,
        disableColumnMenu: true,
        valueFormatter: value => (isValid(value) ? format(value, 'yyyy-MM-dd') : value),
      },
      {
        field: 'site_group',
        headerName: siteGroupTerm.singular,
        flex: 1,
        minWidth: 200,
        disableColumnMenu: true,
        valueGetter: value => _.get(value, 'name', '') + ' - ' + _.get(value, 'description', ''),
      },
      {
        field: 'total_price_charged_to_customer',
        headerName: t('projectServices.billing.columns.revenue'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'total_price_paid_to_crew',
        headerName: t('projectServices.billing.columns.installerCost'),
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'total_extra_paid_to_crew',
        headerName: t('projectServices.billing.columns.installerCostExtra'),
        width: 160,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'profit',
        headerName: t('projectServices.billing.columns.profit'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
    ],
    [t, siteGroupTerm],
  );

  // Totals pinned row
  const revenue = rows.reduce(getTotalReducer('total_price_charged_to_customer'), 0.0);
  const cost = rows.reduce(getTotalReducer('total_price_paid_to_crew'), 0.0);
  const extraCost = rows.reduce(getTotalReducer('total_extra_paid_to_crew'), 0.0);
  const profit = rows.reduce(getTotalReducer('profit'), 0.0);

  const pinnedRows = useMemo(
    () => ({
      bottom: [
        {
          id: '__totals__',
          service_date: t('projectServices.billing.totals'),
          site_group: null,
          total_price_charged_to_customer: revenue,
          total_price_paid_to_crew: cost,
          total_extra_paid_to_crew: extraCost,
          profit,
        },
      ],
    }),
    [revenue, cost, extraCost, profit, t],
  );

  // Excel export columns in legacy immutable format
  const excelColumns = fromJS([
    {
      id: 'service_date',
      name: t('projectServices.billing.columns.installDate'),
      cellRenderer: value => (isValid(value) ? format(value, 'yyyy-MM-dd') : value),
    },
    {
      id: 'site_group',
      name: siteGroupTerm.singular,
      cellRenderer: (value, row) => _.get(value, 'name', '') + ' - ' + _.get(value, 'description', ''),
    },
    {
      id: 'total_price_charged_to_customer',
      name: t('projectServices.billing.columns.revenue'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'total_price_paid_to_crew',
      name: t('projectServices.billing.columns.installerCost'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'total_extra_paid_to_crew',
      name: t('projectServices.billing.columns.installerCostExtra'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'profit',
      name: t('projectServices.billing.columns.profit'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
  ]);

  const excelFooter = [
    t('projectServices.billing.totals'),
    '',
    '$' + formatNumberWithCommas(revenue),
    '$' + formatNumberWithCommas(cost),
    '$' + formatNumberWithCommas(extraCost),
    '$' + formatNumberWithCommas(profit),
  ];

  const exportButton = (
    <InstallServicesBillingExcelButton
      title={`billing_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}_date_summary`}
      firstLine={t('projectServices.billing.excelTitleDate', { term: siteGroupTerm.singular })}
      startDate={format(startDate, 'yyyy-MM-dd')}
      endDate={format(endDate, 'yyyy-MM-dd')}
      columns={excelColumns}
      data={fromJS(rows)}
      footer={excelFooter}
    />
  );

  const showDatePicker = !externalStart;

  const toolbarContent =
    extraToolbarContent ||
    (showDatePicker ? (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ) : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar apiRef={apiRef} showQuickFilter toolbarContent={toolbarContent} toolbarActions={exportButton} />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={row => row.id ?? `${row.service_date}_${_.get(row, 'site_group.id', '')}`}
        pinnedRows={rows.length > 0 ? pinnedRows : undefined}
        initialState={{
          sorting: { sortModel: [{ field: 'service_date', sort: 'desc' }] },
        }}
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        density="compact"
        getRowHeight={() => 'auto'}
        getRowClassName={params => (params.row.id === '__totals__' ? 'totals-row' : '')}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'var(--grid-header-bg)',
            borderTop: '1px solid var(--grid-header-border)',
          },
          '& .totals-row': { fontWeight: 'bold' },
        }}
      />
    </Box>
  );
}

export default DateBillingTable;
